import React, { useState, useMemo, useEffect, type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import CommentIcon from '@atlaskit/icon/glyph/comment';
import Lozenge from '@atlaskit/lozenge';
import { Box, Flex, Text, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import { AsyncConnectSlackWorkspaceModal } from '@atlassian/jira-servicedesk-incident-management-connect-slack-workspace/src/ui/connect-slack-workspace-modal/async.tsx';
import { SettingsCardComponent } from '@atlassian/jira-servicedesk-settings-common/src/ui/settings-card-component';
import { toProjectId } from '@atlassian/jira-shared-types/src/general.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { MSTEAM_APP_TYPE, SLACK_APP_TYPE } from '../../common/constants';
import {
	settingsCardComponentStyles,
	sectionParagraphStyles,
	fieldDescriptionStyles,
} from '../../common/ui';
import { SectionTitle } from '../../common/ui/section-title';
import {
	useSlackWorkspaceConnections,
	useMsTeamsWorkspaceConnections,
} from '../../services/main.tsx';
import type { CommunicationWorkspaceConnection } from '../../services/types.tsx';
import messages from './messages';
import { WorkspaceConnectionSettings } from './slack-workplace-connection-settings';
import { WorkspaceConnectionSummary } from './slack-workspace-connection-summary';
import { WorkspaceCommunicationConnection } from './workspace-communication-connection';

export const CommunicationBridgesSection = () => {
	const { formatMessage } = useIntl();
	const { cloudId } = useTenantContext();
	const { data: projectContext } = useProjectContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const projectId = projectContext ? toProjectId(projectContext.projectId.toString()) : undefined;
	const [{ slackWorkspaceConnectionsData }, { fetchSlackWorkspaceConnectionsForProject }] =
		useSlackWorkspaceConnections();

	const [isSlackWorkSpaceConnectionExpanded, setIsSlackWorkSpaceConnectionExpanded] =
		useState(false);
	const [isSlackWorkspaceModalOpen, setIsSlackWorkspaceModalOpen] = useState(false);

	const [isMsTeamsWorkspaceConnectionExpanded, setIsMsTeamsWorkspaceConnectionExpanded] =
		useState(false);

	const [{ msTeamsWorkspaceConnectionsData }, { fetchMsTeamWorkspaceConnectionForProject }] =
		useMsTeamsWorkspaceConnections();

	useEffect(() => {
		fetchSlackWorkspaceConnectionsForProject({
			cloudId,
			projectId,
			analyticsEvent: createAnalyticsEvent({}),
		});
	}, [cloudId, projectId, createAnalyticsEvent, fetchSlackWorkspaceConnectionsForProject]);

	useEffect(() => {
		fetchMsTeamWorkspaceConnectionForProject({ projectId });
	}, [projectId, fetchMsTeamWorkspaceConnectionForProject]);

	const hasSlackWorkspacesConnected = useMemo(
		() => slackWorkspaceConnectionsData !== undefined && slackWorkspaceConnectionsData.length !== 0,
		[slackWorkspaceConnectionsData],
	);

	const hasMsTeamsWorkspacesConnected = useMemo(
		() =>
			msTeamsWorkspaceConnectionsData !== undefined &&
			Array.isArray(msTeamsWorkspaceConnectionsData) &&
			msTeamsWorkspaceConnectionsData.length !== 0,
		[msTeamsWorkspaceConnectionsData],
	);

	const onConnectWorkspaceButtonClick = (_: MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
		setIsSlackWorkspaceModalOpen(true);
		fireUIAnalytics(analyticsEvent, 'button clicked', 'openConnectSlackWorkspaceModal');
	};

	const getEnabledWorkspaceCount = (
		workspaceConnections: CommunicationWorkspaceConnection[] | undefined,
	): number => {
		if (workspaceConnections !== undefined && Array.isArray(workspaceConnections)) {
			const enabledWorkspaces = workspaceConnections.filter((connection) => connection.enabled);
			return enabledWorkspaces.length;
		}
		return 0;
	};

	const onWorkspaceConnected = () => {
		fetchSlackWorkspaceConnectionsForProject({
			cloudId,
			projectId,
			analyticsEvent: createAnalyticsEvent({}),
		});
		setIsSlackWorkspaceModalOpen(false);
	};

	return (
		<>
			{isSlackWorkspaceModalOpen && (
				<AsyncConnectSlackWorkspaceModal
					onClose={() => setIsSlackWorkspaceModalOpen(false)}
					onConnect={onWorkspaceConnected}
				/>
			)}
			<SettingsCardComponent
				title={
					ff('chatops-pp-banners_1o319') ? (
						<Flex alignItems="start">
							<SectionTitle
								icon={<CommentIcon label="" />}
								message={formatMessage(messages.communicationBridgesSectionHeading)}
							/>
							<Box xcss={premiumBadgeStyles}>
								<Lozenge appearance="new">{formatMessage(messages.premiumLozenge)}</Lozenge>
							</Box>
						</Flex>
					) : (
						<SectionTitle
							icon={<CommentIcon label="" />}
							message={formatMessage(messages.communicationBridgesSectionHeading)}
						/>
					)
				}
				styles={settingsCardComponentStyles}
			>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766 */}
				<Box xcss={[sectionParagraphStyles, fieldDescriptionStyles]}>
					{ff('agni-replace-typography-css-with-tokens_cgl2g') ? (
						<Text>{formatMessage(messages.communicationBridgesSectionDescription)}</Text>
					) : (
						// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
						<span>{formatMessage(messages.communicationBridgesSectionDescription)}</span>
					)}
				</Box>
				<ExpandablePanelContainer>
					<WorkspaceConnectionSummary
						appType={SLACK_APP_TYPE}
						enabledWorkspacesCount={getEnabledWorkspaceCount(slackWorkspaceConnectionsData)}
						hasWorkspacesConnected={hasSlackWorkspacesConnected}
						isExpanded={isSlackWorkSpaceConnectionExpanded}
						setIsExpanded={setIsSlackWorkSpaceConnectionExpanded}
					/>
					{isSlackWorkSpaceConnectionExpanded && (
						<WorkspaceConnectionSettings
							workspaceConnections={slackWorkspaceConnectionsData}
							hasWorkspacesConnected={hasSlackWorkspacesConnected}
							onConnectWorkspaceButtonClick={onConnectWorkspaceButtonClick}
							appType={SLACK_APP_TYPE}
						/>
					)}
				</ExpandablePanelContainer>

				<ExpandablePanelContainer>
					<WorkspaceConnectionSummary
						appType={MSTEAM_APP_TYPE}
						enabledWorkspacesCount={getEnabledWorkspaceCount(msTeamsWorkspaceConnectionsData)}
						hasWorkspacesConnected={hasMsTeamsWorkspacesConnected}
						isExpanded={isMsTeamsWorkspaceConnectionExpanded}
						setIsExpanded={setIsMsTeamsWorkspaceConnectionExpanded}
					/>
					{isMsTeamsWorkspaceConnectionExpanded && (
						<WorkspaceConnectionSettings
							hasWorkspacesConnected={hasMsTeamsWorkspacesConnected}
							workspaceConnections={msTeamsWorkspaceConnectionsData}
							appType={MSTEAM_APP_TYPE}
						/>
					)}
				</ExpandablePanelContainer>

				<WorkspaceCommunicationConnection />
			</SettingsCardComponent>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ExpandablePanelContainer = styled.div({
	borderRadius: '3px',
	backgroundColor: token('elevation.surface.raised', 'transparent'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	border: `1px solid ${token('color.border', colors.N40)}`,
	boxSizing: 'border-box',
	margin: `${token('space.100', '8px')} 0`,
});

const premiumBadgeStyles = xcss({ marginTop: 'space.025' });
